import React, { useState, useEffect, useRef } from 'react';
import {
    Typography, Box, Tabs, Tab, Button, Dialog, DialogTitle, DialogContent,
    DialogActions, TextField, IconButton, Fab, CircularProgress,
    Fade
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { isAdminMode } from '../utils/auth';

interface StudentPerformer {
    id: number;
    name: string;
    stream: string;
    percentage: number;
    imageSrc?: string;
}

interface PerformersData {
    [year: string]: {
        Xth: StudentPerformer[];
        XIIth: StudentPerformer[];
    };
}

// Custom styled components with animations
const YearTab = styled(Tab)({
    fontWeight: 600,
    padding: '12px 24px',
    color: '#64748b',
    fontSize: '0.95rem',
    textTransform: 'none',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        bottom: 0,
        left: '50%',
        width: 0,
        height: '3px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: 'translateX(-50%)',
    },
    '&:hover': {
        color: '#4338ca',
        backgroundColor: 'rgba(99, 102, 241, 0.04)',
        transform: 'translateY(-1px)',
    },
    '&.Mui-selected': {
        color: '#4338ca',
        fontWeight: 700,
        '&::before': {
            width: '100%',
        },
    },
});

const SectionHeading = styled(Typography)({
    fontSize: '1.25rem',
    fontWeight: 700,
    color: '#1e293b',
    marginBottom: '24px',
    position: 'relative',
    display: 'inline-block',
    '&:after': {
        content: '""',
        position: 'absolute',
        bottom: '-8px',
        left: 0,
        width: '100%',
        height: '4px',
        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        borderRadius: '2px',
        animation: 'slideIn 0.6s ease-out',
    },
    '@keyframes slideIn': {
        from: {
            width: 0,
            opacity: 0,
        },
        to: {
            width: '60px',
            opacity: 1,
        },
    },
});

const StyledButton = styled(Button)({
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: '12px',
    padding: '8px 20px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.25)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 25px rgba(102, 126, 234, 0.35)',
    },
    '&:active': {
        transform: 'translateY(0)',
    },
});

const StyledFab = styled(Fab)({
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    boxShadow: '0 8px 25px rgba(102, 126, 234, 0.35)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
        transform: 'translateY(-3px) scale(1.05)',
        boxShadow: '0 12px 35px rgba(102, 126, 234, 0.45)',
    },
});

const AnimatedContainer = styled('div')({
    animation: 'fadeInUp 0.6s ease-out',
    '@keyframes fadeInUp': {
        from: {
            opacity: 0,
            transform: 'translateY(30px)',
        },
        to: {
            opacity: 1,
            transform: 'translateY(0)',
        },
    },
});

const BASE_URL = import.meta.env.VITE_BASE_URL ?? '';

const StudentPerformers: React.FC = () => {
    const [performersData, setPerformersData] = useState<PerformersData>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedYear, setSelectedYear] = useState<string>('');
    const [editingStudent, setEditingStudent] = useState<StudentPerformer | null>(null);
    const [currentClass, setCurrentClass] = useState<'Xth' | 'XIIth' | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [formData, setFormData] = useState<StudentPerformer>({
        id: 0,
        name: '',
        stream: '',
        percentage: 0,
        imageSrc: ''
    });
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string>('');
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Helper function to normalize server response case
    const normalizeServerData = (serverData: any): PerformersData => {
        const normalized: PerformersData = {};

        Object.keys(serverData).forEach(year => {
            const yearData = serverData[year];
            normalized[year] = {
                Xth: yearData.xth || yearData.Xth || [],
                XIIth: yearData.xiIth || yearData.XIIth || yearData.xiith || []
            };
        });

        return normalized;
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const response = await fetch(`${BASE_URL}/api/StudentPerformers`);
                if (!response.ok) throw new Error('Failed to fetch data');
                const data = await response.json();

                console.log('Raw server response:', data);

                const yearsData = data.years || {};
                const normalizedData = normalizeServerData(yearsData);

                console.log('Normalized data:', normalizedData);

                const years = Object.keys(normalizedData).sort((a, b) => parseInt(b) - parseInt(a));
                setPerformersData(normalizedData);
                setSelectedYear(years[0] || '');
            } catch (err: any) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const handleYearChange = (_event: React.SyntheticEvent, newValue: string) => {
        setSelectedYear(newValue);
    };

    const openCreateDialog = (classType: 'Xth' | 'XIIth') => {
        console.log("Opening create dialog for class:", classType);
        setCurrentClass(classType);
        setEditingStudent(null);
        setFormData({
            id: 0,
            name: '',
            stream: '',
            percentage: 0,
            imageSrc: ''
        });
        setImageFile(null);
        setImagePreview('');
        setDialogOpen(true);
    };

    const openEditDialog = (student: StudentPerformer, classType: 'Xth' | 'XIIth') => {
        setCurrentClass(classType);
        setEditingStudent(student);
        setFormData({ ...student });
        setImageFile(null);

        if (student.imageSrc) {
            if (student.imageSrc.startsWith('http') || student.imageSrc.startsWith('/')) {
                setImagePreview(student.imageSrc);
            } else {
                setImagePreview(`${BASE_URL}/public/StudentImages/${student.imageSrc}`);
            }
        } else {
            setImagePreview('');
        }

        setDialogOpen(true);
    };

    const closeDialog = () => {
        setDialogOpen(false);
        setEditingStudent(null);
        setCurrentClass(null);
        setFormData({
            id: 0,
            name: '',
            stream: '',
            percentage: 0,
            imageSrc: ''
        });
        setImageFile(null);
        setImagePreview('');
    };

    const handleFormChange = (field: keyof StudentPerformer, value: string | number) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const allowedTypes = ['image/jpeg', 'image/png', 'image/gif'];
            if (!allowedTypes.includes(file.type)) {
                alert('Only JPG, PNG, and GIF images are allowed');
                return;
            }

            if (file.size > 5 * 1024 * 1024) {
                alert('File size exceeds 5MB limit');
                return;
            }

            setImageFile(file);
            const previewUrl = URL.createObjectURL(file);
            setImagePreview(previewUrl);
        }
    };

    const triggerFileInput = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const getImageUrl = (imagePath?: string) => {
        if (!imagePath) return '/placeholder-student.jpg';

        if (imagePath.startsWith('http') || imagePath.startsWith('/')) {
            return imagePath;
        }

        return `${BASE_URL}/public/topperformers/${imagePath}`;
    };

    const uploadImage = async (): Promise<string> => {
        if (!imageFile) return formData.imageSrc || '';

        try {
            setUploading(true);
            const uploadFormData = new FormData();
            uploadFormData.append('image', imageFile);

            const response = await fetch(`${BASE_URL}/api/StudentPerformers/upload`, {
                method: 'POST',
                body: uploadFormData
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Upload failed: ${errorText}`);
            }

            const result = await response.json();
            return result.imageUrl;
        } catch (error) {
            console.error('Image upload failed:', error);
            throw error;
        } finally {
            setUploading(false);
        }
    };

    const saveStudent = async () => {
        try {
            let imageFilename = formData.imageSrc || '';

            if (imageFile) {
                imageFilename = await uploadImage();
            }

            const studentData = {
                ...formData,
                imageSrc: imageFilename
            };

            const serverClassName = currentClass === 'Xth' ? 'xth' : 'xiIth';

            if (editingStudent) {
                const response = await fetch(`${BASE_URL}/api/StudentPerformers/${selectedYear}/${serverClassName}/${formData.id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(studentData)
                });

                if (!response.ok) throw new Error('Failed to update student');

                const updatedStudent = await response.json();
                updateStudentInState(updatedStudent);
            } else {
                const response = await fetch(`${BASE_URL}/api/StudentPerformers/${selectedYear}/${serverClassName}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(studentData)
                });

                if (!response.ok) throw new Error('Failed to create student');

                const createdStudent = await response.json();
                addStudentToState(createdStudent);
            }

            closeDialog();
        } catch (err) {
            console.error('Failed to save student:', err);
            setError('Failed to save student');
        }
    };

    const deleteStudent = async (student: StudentPerformer, classType: 'Xth' | 'XIIth') => {
        if (!confirm('Are you sure you want to delete this student?')) return;

        try {
            const serverClassName = classType === 'Xth' ? 'xth' : 'xiIth';

            const response = await fetch(`${BASE_URL}/api/StudentPerformers/${selectedYear}/${serverClassName}/${student.id}`, {
                method: 'DELETE'
            });

            if (!response.ok) throw new Error('Failed to delete student');

            removeStudentFromState(student.id, classType);
        } catch (err) {
            console.error('Failed to delete student:', err);
            setError('Failed to delete student');
        }
    };

    const updateStudentInState = (student: StudentPerformer) => {
        setPerformersData(prev => {
            const newData = { ...prev };
            if (newData[selectedYear] && currentClass) {
                newData[selectedYear] = {
                    ...newData[selectedYear],
                    [currentClass]: newData[selectedYear][currentClass].map(s =>
                        s.id === student.id ? student : s
                    )
                };
            }
            return newData;
        });
    };

    const addStudentToState = (student: StudentPerformer) => {
        setPerformersData(prev => {
            const newData = { ...prev };

            if (!newData[selectedYear]) {
                newData[selectedYear] = { Xth: [], XIIth: [] };
            }

            return {
                ...newData,
                [selectedYear]: {
                    ...newData[selectedYear],
                    [currentClass!]: [
                        ...newData[selectedYear][currentClass!],
                        student
                    ]
                }
            };
        });
    };

    const removeStudentFromState = (id: number, classType: 'Xth' | 'XIIth') => {
        setPerformersData(prev => {
            const newData = { ...prev };
            if (newData[selectedYear]) {
                return {
                    ...newData,
                    [selectedYear]: {
                        ...newData[selectedYear],
                        [classType]: newData[selectedYear][classType].filter(s => s.id !== id)
                    }
                };
            }
            return newData;
        });
    };

    if (loading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="200px"
                sx={{
                    '& .MuiCircularProgress-root': {
                        color: '#667eea',
                    }
                }}
            >
                <CircularProgress size={48} thickness={4} />
            </Box>
        );
    }

    if (error) return (
        <div style={{
            color: '#ef4444',
            padding: '20px',
            textAlign: 'center',
            fontSize: '1.1rem',
            fontWeight: 500
        }}>
            Error: {error}
        </div>
    );

    if (!selectedYear || !performersData[selectedYear]) return (
        <div style={{
            color: '#64748b',
            padding: '20px',
            textAlign: 'center',
            fontSize: '1.1rem'
        }}>
            No data available
        </div>
    );

    const currentData = performersData[selectedYear];
    const years = Object.keys(performersData).sort((a, b) => parseInt(b) - parseInt(a));

    console.log('Current data for year', selectedYear, ':', currentData);

    return (
        <div className="  flex justify-center items-center m-10">
            <div className="md:w-[80%] w-[90%] ">
                <AnimatedContainer style={{ margin: '0 24px', minHeight: '100vh' }}>
                    <div style={{
                        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                        borderRadius: '20px',
                        padding: '32px',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                        marginBottom: '24px'
                    }}>
                        <Fade in timeout={800}>
                            <Box className="text-center ">
                                <Typography
                                    variant="h2"
                                    sx={{
                                        fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4rem' },
                                        fontWeight: 800,
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        backgroundClip: 'text',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        letterSpacing: '-0.02em'
                                    }}
                                >
                                    Student Performers
                                </Typography>
                                <div className='flex justify-center items-center'>

                                    <Tabs
                                        value={selectedYear}
                                        onChange={handleYearChange}
                                        variant="scrollable"
                                        scrollButtons="auto"
                                        sx={{
                                            '& .MuiTabs-indicator': {
                                                display: 'none'
                                            }
                                        }}
                                    >
                                        {years.map(year => (
                                            <YearTab key={year} label={year} value={year} />
                                        ))}
                                    </Tabs>
                                </div>
                            </Box>
                        </Fade>

                    </div>

                    <div style={{
                        width: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '32px'
                    }}>
                        {(['Xth', 'XIIth'] as const).map((classType, index) => (
                            <div
                                key={classType}
                                style={{
                                    background: '#ffffff',
                                    borderRadius: '20px',
                                    padding: '32px',
                                    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.08)',
                                    border: '1px solid #f1f5f9',
                                    animation: `slideInLeft 0.6s ease-out ${index * 0.2}s both`,
                                }}
                            >
                                <div className='text-center w-auto flex justify-center items-center'>
                                    <SectionHeading  >
                                        Class {classType === 'Xth' ? 'X' : 'XII'} Toppers
                                    </SectionHeading>

                                    {isAdminMode() && (
                                        <StyledButton
                                            variant="contained"
                                            size="small"
                                            startIcon={<AddIcon />}
                                            onClick={() => openCreateDialog(classType)}
                                        >
                                            Add Student
                                        </StyledButton>
                                    )}
                                </div>

                                <div className='flex justify-center items-center' style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    gap: '20px',
                                    flexWrap: 'wrap',
                                    justifyContent: 'center',
                                }}>
                                    {(currentData[classType] || []).map((student, studentIndex) => (
                                        <div
                                            key={`${classType}-${student.id}`}
                                            style={{
                                                animation: `fadeInScale 0.5s ease-out ${studentIndex * 0.1}s both`
                                            }}

                                        >
                                            <StudentCard
                                                student={student}
                                                getImageUrl={getImageUrl}
                                                onEdit={(s) => openEditDialog(s, classType)}
                                                onDelete={(s) => deleteStudent(s, classType)}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Enhanced Dialog */}
                    <Dialog
                        open={dialogOpen}
                        onClose={closeDialog}
                        fullWidth
                        maxWidth="sm"
                        PaperProps={{
                            sx: {
                                borderRadius: '20px',
                                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
                                overflow: 'hidden'
                            }
                        }}
                    >
                        <DialogTitle sx={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            fontWeight: 700,
                            fontSize: '1.25rem'
                        }}>
                            {editingStudent ? 'Edit Student' : 'Add New Student'}
                            <IconButton
                                onClick={closeDialog}
                                sx={{
                                    position: 'absolute',
                                    right: 8,
                                    top: 8,
                                    color: 'rgba(255, 255, 255, 0.8)',
                                    '&:hover': {
                                        color: 'white',
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                    }
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent dividers sx={{ padding: '32px' }}>
                            <Box sx={{ mt: 2 }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                    <div style={{
                                        width: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center'
                                    }}>
                                        <Box
                                            sx={{
                                                width: 160,
                                                height: 160,
                                                margin: '0 auto',
                                                position: 'relative',
                                                cursor: 'pointer',
                                                border: '3px dashed #cbd5e1',
                                                borderRadius: '20px',
                                                overflow: 'hidden',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                '&:hover': {
                                                    borderColor: '#667eea',
                                                    transform: 'scale(1.02)',
                                                    boxShadow: '0 8px 25px rgba(102, 126, 234, 0.15)'
                                                }
                                            }}
                                            onClick={triggerFileInput}
                                        >
                                            {imagePreview ? (
                                                <img
                                                    src={imagePreview}
                                                    alt="Preview"
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover'
                                                    }}
                                                />
                                            ) : (
                                                <PhotoCameraIcon
                                                    fontSize="large"
                                                    sx={{ color: '#94a3b8', fontSize: '3rem' }}
                                                />
                                            )}
                                        </Box>
                                        <input
                                            type="file"
                                            hidden
                                            ref={fileInputRef}
                                            accept="image/jpeg, image/png, image/gif"
                                            onChange={handleImageSelect}
                                        />
                                        <Typography
                                            variant="caption"
                                            display="block"
                                            textAlign="center"
                                            mt={2}
                                            sx={{
                                                color: '#64748b',
                                                fontWeight: 500
                                            }}
                                        >
                                            Click to upload student photo
                                        </Typography>
                                    </div>

                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '16px'
                                    }}>
                                        <div style={{
                                            display: 'flex',
                                            gap: '16px',
                                            flexDirection: 'column'
                                        }}>
                                            <TextField
                                                label="Full Name"
                                                fullWidth
                                                value={formData.name}
                                                onChange={(e) => handleFormChange('name', e.target.value)}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '12px',
                                                        '&:hover fieldset': {
                                                            borderColor: '#667eea',
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: '#667eea',
                                                        },
                                                    },
                                                }}
                                            />
                                            <TextField
                                                label="Stream"
                                                fullWidth
                                                value={formData.stream}
                                                onChange={(e) => handleFormChange('stream', e.target.value)}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '12px',
                                                        '&:hover fieldset': {
                                                            borderColor: '#667eea',
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: '#667eea',
                                                        },
                                                    },
                                                }}
                                            />
                                        </div>

                                        <TextField
                                            label="Percentage"
                                            fullWidth
                                            type="number"
                                            value={formData.percentage}
                                            onChange={(e) => handleFormChange('percentage', parseFloat(e.target.value) || 0)}
                                            inputProps={{ min: 0, max: 100, step: 0.01 }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '12px',
                                                    '&:hover fieldset': {
                                                        borderColor: '#667eea',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: '#667eea',
                                                    },
                                                },
                                            }}
                                        />
                                    </div>
                                </div>
                            </Box>
                        </DialogContent>
                        <DialogActions sx={{ padding: '24px 32px' }}>
                            <Button
                                onClick={closeDialog}
                                sx={{
                                    color: '#64748b',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    borderRadius: '12px',
                                    padding: '8px 20px'
                                }}
                            >
                                Cancel
                            </Button>
                            <StyledButton
                                onClick={saveStudent}
                                disabled={uploading}
                                startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : null}
                            >
                                {editingStudent ? 'Update' : 'Create'}
                            </StyledButton>
                        </DialogActions>
                    </Dialog>

                    {isAdminMode() && (
                        <StyledFab
                            aria-label="add"
                            onClick={() => openCreateDialog('Xth')}
                            sx={{
                                position: 'absolute',
                                bottom: 24,
                                right: 24,
                                zIndex: 1000
                            }}
                        >
                            <AddIcon />
                        </StyledFab>
                    )}

                    <style>
                        {`
                    @keyframes slideInLeft {
                        from {
                            opacity: 0;
                            transform: translateX(-50px);
                        }
                        to {
                            opacity: 1;
                            transform: translateX(0);
                        }
                    }
                    
                    @keyframes fadeInScale {
                        from {
                            opacity: 0;
                            transform: scale(0.8);
                        }
                        to {
                            opacity: 1;
                            transform: scale(1);
                        }
                    }
                `}
                    </style>
                </AnimatedContainer>
            </div>
        </div>
    );
};

const StudentCard: React.FC<{
    student: StudentPerformer;
    getImageUrl: (path?: string) => string;
    onEdit?: (student: StudentPerformer) => void;
    onDelete?: (student: StudentPerformer) => void;
}> = ({ student, getImageUrl, onEdit, onDelete }) => {
    console.log('Rendering StudentCard for:', student.name);

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            borderRadius: '20px',
            overflow: 'hidden',
            boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
            backgroundColor: '#ffffff',
            width: '200px',
            height: '300px',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            border: '1px solid #f1f5f9',
            cursor: 'pointer'
        }}
            onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px) scale(1.02)';
                e.currentTarget.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.15)';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.12)';
            }}
        >
            {/* Admin controls */}
            {isAdminMode() && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        display: 'flex',
                        gap: 0.5,
                        zIndex: 2,
                        opacity: 0,
                        transition: 'opacity 0.3s ease',
                        '.student-card:hover &': {
                            opacity: 1
                        }
                    }}
                    className="admin-controls"
                >
                    <IconButton
                        size="small"
                        onClick={() => onEdit?.(student)}
                        sx={{
                            backgroundColor: 'rgba(255,255,255,0.95)',
                            backdropFilter: 'blur(8px)',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            color: '#667eea',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                backgroundColor: '#667eea',
                                color: 'white',
                                transform: 'scale(1.1)'
                            }
                        }}
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => onDelete?.(student)}
                        sx={{
                            backgroundColor: 'rgba(255,255,255,0.95)',
                            backdropFilter: 'blur(8px)',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            color: '#ef4444',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                backgroundColor: '#ef4444',
                                color: 'white',
                                transform: 'scale(1.1)'
                            }
                        }}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Box>
            )}

            <div style={{
                width: '100%',
                height: '70%',
                position: 'relative',
                overflow: 'hidden'
            }}>
                <img
                    src={getImageUrl(student.imageSrc)}
                    alt={student.name}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'top center',
                        display: 'block',
                        transition: 'transform 0.3s ease'
                    }}
                    onError={(e) => {
                        (e.target as HTMLImageElement).src = '/placeholder-student.jpg';
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                    }}
                />

                {/* Gradient overlay */}
                <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '30%',
                    background: 'linear-gradient(transparent, rgba(0, 0, 0, 0.1))',
                    pointerEvents: 'none'
                }} />
            </div>

            <div style={{
                height: '30%',
                padding: '16px 12px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
            }}>
                <div style={{
                    textAlign: 'center',
                    fontSize: '0.95rem',
                    fontWeight: 700,
                    color: '#1e293b',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    marginBottom: '8px',
                    letterSpacing: '0.025em'
                }}>
                    {student.name}
                </div>

                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Typography
                        variant="body2"
                        sx={{
                            color: '#64748b',
                            fontSize: '0.85rem',
                            fontWeight: 500
                        }}
                    >
                        {student.stream}
                    </Typography>
                    <div style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '0.85rem',
                        fontWeight: 700,
                        boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
                    }}>
                        {student.percentage}%
                    </div>
                </div>
            </div>

            <style>
                {`
                    .student-card:hover .admin-controls {
                        opacity: 1 !important;
                    }
                `}
            </style>
        </div>
    );
};

export default StudentPerformers;